import { WasmService, GraphHandle, ParseError } from './services/WasmService';
import { Editor } from './components/Editor';
import { Preview } from './components/Preview';
import { downloadSVG, downloadPNG, downloadPDF } from './utils/download';

const EXAMPLES = {
    simple: `flowchart TD
    A[Start] --> B{Is it?}
    B -->|Yes| C[OK]
    C --> D[Rethink]
    D --> B
    B -->|No| E[End]`,

    complex: `---
title: Gift shopping
---
flowchart TB
    A[Christmas] -->|Get money| B(Go shopping)
    B --> C{Let me think}
    C -->|One| D[Laptop]
    C -->|Two| E[iPhone]
    C -->|Three| F[fa:fa-car Car]
    D --> G[Result one]
    E --> G
    F --> G`,
};

export class App {
    private wasmService: WasmService;
    private editor: Editor;
    private preview: Preview;
    private currentHandle: GraphHandle | null = null;

    constructor() {
        this.wasmService = new WasmService();
        this.editor = new Editor({
            onChange: this.handleEditorChange.bind(this),
            onDebugGridChange: this.handleDebugGridChange.bind(this),
        });
        this.preview = new Preview();

        this.setupUI();
    }

    async init(): Promise<void> {
        try {
            console.log('Initializing WASM module...');
            await this.wasmService.init();
            console.log('WASM module ready');

            // Load simple example by default
            this.loadExample('simple');
        } catch (error) {
            console.error('Failed to initialize app:', error);
            this.showError('Failed to initialize WASM module');
        }
    }

    private setupUI(): void {
        // Example buttons
        const simpleBtn = document.getElementById('btn-simple');
        const complexBtn = document.getElementById('btn-complex');
        const clearBtn = document.getElementById('btn-clear');

        simpleBtn?.addEventListener('click', () => this.loadExample('simple'));
        complexBtn?.addEventListener('click', () => this.loadExample('complex'));
        clearBtn?.addEventListener('click', () => this.clearInput());

        // Download buttons
        document.getElementById('download-svg')?.addEventListener('click', () => this.downloadSVG());
        document.getElementById('download-png')?.addEventListener('click', () => this.downloadPNG());
        document.getElementById('download-pdf')?.addEventListener('click', () => this.downloadPDF());

        // Zoom controls
        document.getElementById('zoom-in')?.addEventListener('click', () => this.preview.zoomBy(0.1));
        document.getElementById('zoom-out')?.addEventListener('click', () => this.preview.zoomBy(-0.1));
        document.getElementById('zoom-reset')?.addEventListener('click', () => this.preview.resetZoom());

        // Cleanup on page unload
        window.addEventListener('beforeunload', () => {
            if (this.currentHandle !== null) {
                this.wasmService.freeGraph(this.currentHandle);
            }
        });
    }

    private handleEditorChange(value: string): void {
        console.log('handleEditorChange called with value:', value.substring(0, 50) + '...');

        // Clear any previous error markers
        this.editor.clearErrors();

        if (!value.trim()) {
            this.clearOutput();
            return;
        }

        this.renderDiagram(value);
    }

    private handleDebugGridChange(): void {
        const value = this.editor.getValue();
        if (value.trim()) {
            this.renderDiagram(value);
        }
    }

    private renderDiagram(input: string): void {
        console.log('renderDiagram called with input:', input.substring(0, 50) + '...');

        try {
            // Free previous handle if exists
            if (this.currentHandle !== null) {
                this.wasmService.freeGraph(this.currentHandle);
                this.currentHandle = null;
            }

            // Parse the input
            this.currentHandle = this.wasmService.parseMermaid(input);

            // Render SVG
            const debugGrid = this.editor.getDebugGrid();
            const svg = this.wasmService.renderSvg(this.currentHandle, debugGrid);
            this.preview.displaySVG(svg);

            // Enable download buttons
            this.setDownloadButtonsEnabled(true);

            this.hideError();
        } catch (error) {
            console.error('Render error:', error);

            // Check if it's a ParseError with location information
            if (error instanceof ParseError) {
                // Mark the error location in the editor
                this.editor.markError(error.line, error.column);
                this.showError(`Line ${error.line}, Column ${error.column}: ${error.message}`);
            } else {
                this.showError(String(error));
            }

            this.clearOutput();
        }
    }

    private clearOutput(): void {
        this.preview.clear();
        this.setDownloadButtonsEnabled(false);
    }

    private setDownloadButtonsEnabled(enabled: boolean): void {
        const buttons = ['download-svg', 'download-png', 'download-pdf'];
        buttons.forEach((id) => {
            const btn = document.getElementById(id) as HTMLButtonElement;
            if (btn) btn.disabled = !enabled;
        });
    }

    private loadExample(type: 'simple' | 'complex'): void {
        const example = EXAMPLES[type];
        if (example) {
            // setValue will trigger onChange which calls renderDiagram
            this.editor.setValue(example);
        }
    }

    private clearInput(): void {
        this.editor.clear();
        this.editor.clearErrors();
        this.clearOutput();
        this.hideError();

        if (this.currentHandle !== null) {
            this.wasmService.freeGraph(this.currentHandle);
            this.currentHandle = null;
        }
    }

    private async downloadSVG(): Promise<void> {
        if (this.currentHandle === null) return;

        try {
            const svg = this.wasmService.renderSvg(this.currentHandle, false);
            downloadSVG(svg, 'mermaid-diagram.svg');
            console.log('SVG downloaded');
        } catch (error) {
            console.error('Download error:', error);
            this.showError('Failed to download SVG: ' + error);
        }
    }

    private async downloadPNG(): Promise<void> {
        if (this.currentHandle === null) return;

        try {
            const svg = this.wasmService.renderSvg(this.currentHandle, false);
            await downloadPNG(svg, 'mermaid-diagram.png');
            console.log('PNG downloaded');
        } catch (error) {
            console.error('PNG download error:', error);
            this.showError('Failed to download PNG: ' + error);
        }
    }

    private async downloadPDF(): Promise<void> {
        if (this.currentHandle === null) return;

        try {
            console.log('Generating PDF...');
            const pdfBytes = this.wasmService.renderPdf(this.currentHandle);
            downloadPDF(pdfBytes, 'mermaid-diagram.pdf');
            console.log('PDF downloaded');
        } catch (error) {
            console.error('PDF download error:', error);
            this.showError('Failed to download PDF: ' + error);
        }
    }

    private showError(message: string): void {
        const errorDiv = document.getElementById('error-message');
        if (errorDiv) {
            errorDiv.textContent = message;
            errorDiv.classList.add('visible');
        }
    }

    private hideError(): void {
        const errorDiv = document.getElementById('error-message');
        if (errorDiv) {
            errorDiv.classList.remove('visible');
        }
    }

}
